<!-- src/views/pages/administration/reports/muster-roll-report-table.vue -->
<template>
  <a-table
    class="table datatable thead-light dq-report-table"
    :columns="columns"
    :data-source="dataSource"
    :loading="loading"
    :pagination="pagination"
    :scroll="{ x: 2400 }"
    @change="onChange"
    row-key="key"
    size="small"
  >
    <template #bodyCell="{ column, record }">
      <template v-if="column.key === 'Name'">
        <div class="d-flex align-items-center">
          <a href="javascript:void(0);">
            <DqAvatar
              :src="record.Image"
              :name="record.name"
              size-class="avatar-md"
              shape-class="rounded-circle"
            />
          </a>
          <div class="ms-2">
            <p class="text-dark mb-0">{{ record.name }}</p>
            <span class="fs-12">{{ record.payroll_number || "-" }}</span>
          </div>
        </div>
      </template>

      <template v-else-if="column.isMoney">
        {{ currency }}
        {{
          Number(record[column.dataIndex] || 0).toLocaleString(undefined, {
            minimumFractionDigits: 2,
          })
        }}
      </template>

      <template v-else>
        {{ record[column.dataIndex] || "-" }}
      </template>
    </template>
  </a-table>
</template>

<script setup>
import { computed } from "vue";
import DqAvatar from "@/components/shared/DqAvatar.vue";

const props = defineProps({
  rows: { type: Array, default: () => [] },
  loading: { type: Boolean, default: false },
  currency: { type: String, default: "KES" },
  total: { type: Number, default: 0 },
  pageSize: { type: Number, default: 25 },
  current: { type: Number, default: 1 },
});

const emit = defineEmits(["change"]);

const moneyCol = (title, dataIndex, width = 110) => ({
  title,
  dataIndex,
  key: dataIndex,
  width,
  isMoney: true,
  align: "right",
  sorter: (a, b) => (a[dataIndex] || 0) - (b[dataIndex] || 0),
});

const columns = [
  {
    title: "Employee",
    dataIndex: "name",
    key: "Name",
    fixed: "left",
    width: 220,
    sorter: (a, b) => (a.name || "").localeCompare(b.name || ""),
  },
  {
    title: "Days Present",
    dataIndex: "present_days",
    key: "present_days",
    width: 110,
    sorter: (a, b) => (a.present_days || 0) - (b.present_days || 0),
  },
  {
    title: "Days Absent",
    dataIndex: "absent_days",
    key: "absent_days",
    width: 110,
    sorter: (a, b) => (a.absent_days || 0) - (b.absent_days || 0),
  },
  moneyCol("Basic Pay", "basic_pay"),
  moneyCol("House All.", "house_allowance", 100),
  moneyCol("Travel All.", "travel_allowance", 100),
  moneyCol("Bonus", "bonus", 90),
  moneyCol("Overtime", "overtime", 100),
  moneyCol("Gross Pay", "gross_pay", 110),
  moneyCol("PAYE", "paye", 90),
  moneyCol("NSSF", "nssf", 90),
  moneyCol("SHIF", "shif", 90),
  moneyCol("Housing Levy", "housing_levy", 110),
  moneyCol("Advance Rec.", "advance_recovery", 110),
  moneyCol("Shorts", "shorts", 90),
  moneyCol("Loans", "loans", 90),
  moneyCol("Savings", "savings", 90),
  moneyCol("Total Ded.", "total_deductions", 110),
  moneyCol("Net Pay", "net_pay", 110),
  {
    title: "PIN",
    dataIndex: "pin_number",
    key: "pin_number",
    width: 120,
  },
  {
    title: "NSSF No.",
    dataIndex: "nssf_number",
    key: "nssf_number",
    width: 120,
  },
  {
    title: "NHIF No.",
    dataIndex: "nhif_number",
    key: "nhif_number",
    width: 110,
  },
  {
    title: "ID No.",
    dataIndex: "id_number",
    key: "id_number",
    width: 110,
  },
  moneyCol("NSSF Emp.", "nssf_employer", 100),
  moneyCol("Levy Emp.", "housing_levy_employer", 100),
];

const dataSource = computed(() =>
  (props.rows || []).map((r, idx) => ({
    key: r.id || idx,
    Image: r.avatar_url || "",
    name: r.employee_name || "",
    payroll_number: r.payroll_number || r.employee_id || "",
    present_days: r.present_days ?? 0,
    absent_days: r.absent_days ?? 0,
    basic_pay: r.basic_pay ?? 0,
    house_allowance: r.house_allowance ?? 0,
    travel_allowance: r.travel_allowance ?? 0,
    bonus: r.bonus ?? 0,
    overtime: r.overtime ?? 0,
    gross_pay: r.gross_pay ?? r.gross_salary ?? 0,
    paye: r.paye ?? 0,
    nssf: r.nssf ?? 0,
    shif: r.shif ?? 0,
    housing_levy: r.housing_levy ?? 0,
    advance_recovery: r.advance_recovery ?? 0,
    shorts: r.shorts ?? 0,
    loans: r.loans ?? 0,
    savings: r.savings ?? 0,
    total_deductions: r.total_deductions ?? 0,
    net_pay: r.net_pay ?? 0,
    pin_number: r.pin_number || "",
    nssf_number: r.nssf_number || "",
    nhif_number: r.nhif_number || "",
    id_number: r.id_number || "",
    nssf_employer: r.nssf_employer ?? 0,
    housing_levy_employer: r.housing_levy_employer ?? 0,
  })),
);

const pagination = computed(() => ({
  total: props.total,
  pageSize: props.pageSize,
  current: props.current,
  showTotal: (t) => `Total ${t} items`,
}));

const currency = computed(() => props.currency || "KES");

function onChange(pagination, filters, sorter) {
  emit("change", pagination, filters, sorter);
}
</script>

<style scoped>
:deep(.dq-report-table .ant-table) {
  min-width: 2400px;
}

:deep(.dq-report-table .ant-table-cell) {
  vertical-align: middle;
  white-space: nowrap;
  font-size: 12px;
}

@media (max-width: 767.98px) {
  :deep(.dq-report-table .ant-table-cell) {
    white-space: normal;
  }
}
</style>
